import React from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEO, { createBreadcrumbSchema, createFAQSchema, softwareApplicationSchema } from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, CheckCircle, Zap, Image, Target, Palette, BarChart, Clock, Star, Facebook, Globe } from 'lucide-react';

const AIAdCreativeGenerator = () => {
  const faqs = [
    {
      question: "What ad sizes can I generate for Facebook and Instagram?",
      answer: "Our AI generates all standard Facebook and Instagram ad sizes including 1080x1080 (feed), 1200x628 (link ads), 1080x1920 (Stories), and 1920x1080 (landscape). All images are optimized for each platform's requirements."
    },
    {
      question: "Can I create Google Ads display creatives?",
      answer: "Yes! Generate all standard Google Display Network sizes including 300x250, 728x90, 160x600, 336x280, and responsive display ads. Create consistent branding across your entire display campaign."
    },
    {
      question: "How does AI help with A/B testing ad creatives?",
      answer: "Generate dozens of ad variations in minutes for testing. Create different backgrounds, layouts, and visual styles quickly. Our bulk generation lets you test more variations to find winners faster."
    },
    {
      question: "Are these images compliant with ad platform policies?",
      answer: "Yes, our generated images follow platform guidelines. However, you should ensure your ad copy and targeting comply with each platform's advertising policies."
    },
    {
      question: "Can I maintain brand consistency across campaigns?",
      answer: "Absolutely! Use consistent prompts and style guidelines to maintain brand consistency. Generate entire campaigns with matching visual themes and aesthetics."
    }
  ];

  const features = [
    {
      icon: <Facebook className="w-6 h-6" />,
      title: "Facebook & Instagram Ads",
      description: "Generate scroll-stopping ad creatives for feed, Stories, Reels, and carousel formats"
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: "Google Display Ads",
      description: "Create all standard GDN sizes in seconds. Responsive display and banner ads ready to deploy"
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: "High-Converting Creatives",
      description: "AI-generated visuals designed to capture attention and drive clicks"
    },
    {
      icon: <BarChart className="w-6 h-6" />,
      title: "A/B Testing at Scale",
      description: "Generate dozens of variations quickly to find your best-performing creatives"
    },
    {
      icon: <Palette className="w-6 h-6" />,
      title: "Brand Consistency",
      description: "Maintain visual consistency across all platforms and campaigns"
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Launch Campaigns Faster",
      description: "Reduce creative production time from days to minutes"
    }
  ];

  const useCases = [
    "Performance marketing teams scaling ad creative production",
    "E-commerce brands running Facebook and Google campaigns",
    "Agencies managing multiple client ad accounts",
    "Startups with limited design resources",
    "Dropshippers needing rapid creative iteration",
    "Lead generation campaigns requiring fresh visuals",
    "Retargeting campaigns with personalized creatives",
    "Seasonal and holiday advertising campaigns"
  ];

  const adPlatforms = [
    { name: "Facebook Ads", sizes: "1080×1080, 1200×628, 1080×1920" },
    { name: "Instagram Ads", sizes: "1080×1080, 1080×1920, 1080×1350" },
    { name: "Google Display", sizes: "300×250, 728×90, 160×600, 336×280" },
    { name: "LinkedIn Ads", sizes: "1200×627, 1080×1080" },
    { name: "Twitter/X Ads", sizes: "1200×675, 1600×900" },
    { name: "Pinterest Ads", sizes: "1000×1500, 1080×1920" }
  ];

  return (
    <>
      <SEO
        title="AI Ad Creative Generator | Facebook, Google & Instagram Ads"
        description="Generate high-converting ad creatives with AI. Create Facebook ads, Google Display ads, Instagram ads in seconds. A/B test at scale. Used by 3,000+ marketing teams."
        keywords="ai ad creative generator, facebook ad generator, google ads creative, instagram ad maker, ad creative ai, bulk ad creatives, facebook ad images, google display ads generator, marketing creative ai, ad creative tool"
        url="/ai-ad-creative-generator"
        jsonLd={[
          softwareApplicationSchema,
          createBreadcrumbSchema([
            { name: 'Home', url: '/' },
            { name: 'AI Ad Creative Generator', url: '/ai-ad-creative-generator' }
          ]),
          createFAQSchema(faqs)
        ]}
      />
      
      <Header />
      
      <main className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-pink-500/5 to-background" />
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-4 bg-gradient-to-r from-purple-500/20 to-pink-500/20 text-foreground border-0">
                Trusted by 3,000+ Marketing Teams in the US
              </Badge>
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                AI Ad Creative Generator
                <span className="block bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent drop-shadow-sm">
                  Facebook, Google & Instagram Ads
                </span>
              </h1>
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                Generate high-converting ad creatives in seconds. A/B test at scale. 
                Launch campaigns faster with AI-powered visual content for all major ad platforms.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="bg-gradient-to-r from-purple-500 to-pink-500 hover:opacity-90 text-white">
                  <Link to="/try-generator">
                    Create Ad Creatives Free <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <Link to="/gallery">View Ad Examples</Link>
                </Button>
              </div>
              
              {/* Trust Badges */}
              <div className="mt-8 flex flex-wrap justify-center gap-6 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                  <span>4.9/5 from 2,800+ reviews</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Full Commercial Rights</span>
                </div>
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-pink-500" />
                  <span>All Ad Sizes Supported</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Ad Platforms */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              Supported Ad Platforms & Sizes
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {adPlatforms.map((platform, index) => (
                <Card key={index} className="border-0 bg-gradient-to-br from-purple-500/5 to-pink-500/5">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold mb-2">{platform.name}</h3>
                    <p className="text-muted-foreground text-sm">{platform.sizes}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              Why Marketing Teams Choose Our AI Generator
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {features.map((feature, index) => (
                <Card key={index} className="border border-transparent bg-gradient-to-br from-purple-500/5 to-pink-500/5 hover:from-purple-500/10 hover:to-pink-500/10 hover:border-purple-500/20 transition-all shadow-sm hover:shadow-md">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center text-foreground mb-4">
                      {feature.icon}
                    </div>
                    <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                    <p className="text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">
                Perfect For Every Marketing Team
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {useCases.map((useCase, index) => (
                  <div key={index} className="flex items-center gap-3 p-4 rounded-lg bg-gradient-to-r from-purple-500/5 to-pink-500/5">
                    <CheckCircle className="w-5 h-5 text-purple-500 flex-shrink-0" />
                    <span>{useCase}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              Frequently Asked Questions
            </h2>
            <div className="max-w-3xl mx-auto space-y-6">
              {faqs.map((faq, index) => (
                <Card key={index} className="border-0 bg-gradient-to-br from-purple-500/5 to-pink-500/5">
                  <CardContent className="p-6">
                    <h3 className="font-semibold mb-2">{faq.question}</h3>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-6">
                Start Creating High-Converting Ad Creatives Today
              </h2>
              <p className="text-xl text-muted-foreground mb-8">
                Join 3,000+ marketing teams using AI to create ad creatives at scale. 
                A/B test more variations. Launch campaigns faster. Get better results.
              </p>
              <Button asChild size="lg" className="bg-gradient-to-r from-purple-500 to-pink-500 hover:opacity-90 text-white">
                <Link to="/try-generator">
                  Generate Ad Creatives Free <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </>
  );
};

export default AIAdCreativeGenerator;
